import { Label } from "@/components/ui/label";
import type { AlignError } from "@/constants";
import { Input } from "@/pages/components/input";
import { Info } from "lucide-react";
import InfoToolTip from "./info-tooltip";
import { cn } from "@/lib/utils";

type InputProps = {
  label?: string;
  tooltip?: string;
  placeholder?: string;
  id: string;
  value?: string | undefined | null;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onBlurCapture?: () => void;
  error?: string;
  isDisabled?: boolean;
  /**
   * Retained for call-site compatibility only. The error message is now rendered as
   * persistent text below the field instead of inside a hover card, so there is no
   * floating element left to align.
   */
  alignError?: AlignError;
  "data-testid": string;
  className?: string;
  autoComplete?: string;
};

export const InputWithLabel = ({
  label,
  tooltip = "",
  placeholder,
  id,
  value,
  onChange,
  onBlur,
  onBlurCapture,
  error,
  isDisabled = false,
  "data-testid": dataTestId,
  className,
  autoComplete,
}: InputProps) => {
  const errorId = `${id}-error`;
  const hasError = Boolean(error);

  return (
    <div className="grid w-full items-center gap-3">
      {label && (
        <div className="flex flex-row items-center space-x-2">
          <Label htmlFor={id}>{label}</Label>
          {tooltip?.length > 0 ? <InfoToolTip info={tooltip} /> : null}
        </div>
      )}

      <div className="relative flex flex-row items-center space-x-2">
        <Input
          type="text"
          id={id}
          placeholder={placeholder}
          className={cn(
            "border-0 bg-gray-100 focus-visible:ring-gray-200",
            className,
          )}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={() => onBlur?.()}
          onBlurCapture={() => onBlurCapture?.()}
          disabled={isDisabled}
          autoComplete={autoComplete}
          aria-invalid={hasError ? true : undefined}
          aria-describedby={hasError ? errorId : undefined}
          data-testid={dataTestId}
        />
        {/* Decorative: the message itself is announced from the alert below. */}
        {hasError && (
          <Info
            aria-hidden="true"
            className="absolute right-2 text-[11px] text-destructive"
            size={16}
          />
        )}
      </div>

      {/*
        3.3.1 Error Identification — A. Previously the message lived only inside a
        HoverCard whose trigger was a bare <svg>: not focusable, so keyboard users could
        never open it, and never referenced by the input, so screen readers never read it.
        It is now persistent text, tied to the field via aria-describedby, and mounted
        with role="alert" so it is announced the moment it appears.
      */}
      {hasError && (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
};
